import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Board, AppTheme, CanvasSettings, UserProfile, Workspace } from '../types';
import { Icons } from '../constants';
import { GlobalSearch } from './GlobalSearch';
import { NotificationBell } from './notifications/NotificationBell';
import { searchService } from '../services/searchService';
import { SettingsPanel, Sidebar, BoardGrid, BoardList, TemplateWizard, DashboardHeader, QuickActions, RecentlyViewed, SectionTitle } from './home';
import { THEME_STYLES as THEME_STYLES_CONST } from '../constants/theme';

import { generateSlug } from '../utils/slug';
import { useToast } from './ui/ToastProvider';

// Lazy load SmartViewPage for code splitting
const SmartViewPage = lazy(() => import('./SmartView').then(module => ({ default: module.SmartViewPage })));

// Lazy load PromptLibraryPage for code splitting
const PromptLibraryPage = lazy(() => import('./home/PromptLibraryPage').then(module => ({ default: module.PromptLibraryPage })));

// Loading fallback for lazy components
const LoadingFallback = () => (
    <div className="flex items-center justify-center min-h-[400px] w-full">
        <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-500" />
            <p className="text-sm font-medium text-slate-500">Loading Smart View...</p>
        </div>
    </div>
);

import { BoardTemplate } from '../templates';
import { Template } from '../utils/templates';

interface HomePageProps {
    boards: Board[];
    onSelectBoard: (boardId: string) => void;
    onCreateBoard: () => void;
    onCreateBoardFromTemplate?: (template: BoardTemplate | Template) => void;
    onUpdateBoard: (board: Board) => void;
    onDeleteBoard: (boardId: string) => void;
    onRestoreBoard: (boardId: string) => void;
    onDeleteForever: (boardId: string) => void;
    onToggleFavorite: (boardId: string) => void;
    onDuplicateBoard?: (boardId: string) => void;
    currentTheme: AppTheme;
    onThemeChange: (theme: AppTheme) => void;
    canvasSettings: CanvasSettings;
    onCanvasSettingsChange: (settings: CanvasSettings) => void;
    userProfile?: UserProfile | null;
    onUpdateProfile?: (profile: Partial<UserProfile>) => void;
    onImportBoard?: () => void;
    onLogout?: () => void;
    // Workspace props (lifted from internal hook)
    workspaces: Workspace[];
    activeWorkspaceId: string | null;
    onSelectWorkspace: (id: string | null) => void;
    onCreateWorkspace: (name: string, color?: string) => void;
    onDeleteWorkspace: (id: string) => void;
    onRenameWorkspace: (id: string, name: string) => void;
    workspaceColors: string[];
}



export default function HomePage({
    boards,
    onSelectBoard,
    onCreateBoard,
    onUpdateBoard,
    onDeleteBoard,
    onRestoreBoard,
    onDeleteForever,
    onToggleFavorite,
    onDuplicateBoard,
    currentTheme,
    onThemeChange,
    canvasSettings,
    onCanvasSettingsChange,
    userProfile,
    onUpdateProfile,
    onImportBoard: _onImportBoard,
    onLogout,
    onCreateBoardFromTemplate,
    // Workspace props from App.tsx
    workspaces,
    activeWorkspaceId,
    onSelectWorkspace,
    onCreateWorkspace,
    onDeleteWorkspace,
    onRenameWorkspace,
    workspaceColors,
}: HomePageProps) {
    // UI state
    const [activeTab, setActiveTab] = useState('dashboard');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [searchQuery] = useState('');
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [showThemeOptions, setShowThemeOptions] = useState(false);
    const [isTemplateWizardOpen, setIsTemplateWizardOpen] = useState(false);
    const profileMenuRef = useRef<HTMLDivElement>(null);
    const { showSuccess, showError } = useToast();


    const [editingBoard, setEditingBoard] = useState<Board | null>(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);

    // Sorting state
    const [sortBy, setSortBy] = useState<'lastEdited' | 'created' | 'title' | 'itemCount'>('lastEdited');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [filterColor, setFilterColor] = useState<string | null>(null);

    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
    // const _dateString = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

    // Index boards for global search
    useEffect(() => {
        searchService.indexAllBoards(boards);
    }, [boards]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
                setIsProfileMenuOpen(false);
                setShowThemeOptions(false);
            }
            // Only close if we didn't click inside the menu
            const target = event.target as HTMLElement;
            if (openMenuId && !target.closest('.board-menu-container')) {
                setOpenMenuId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openMenuId]);

    const handleEditSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (editingBoard) {
            onUpdateBoard(editingBoard);
            setEditingBoard(null);
        }
    };

    const handleShareBoard = async (board: Board) => {
        const url = `${window.location.origin}/board/${board.slug || board.id}`;
        try {
            await navigator.clipboard.writeText(url);
            showSuccess('Link copied to clipboard');
        } catch (err) {
            console.error('Failed to copy link', err);
            showError('Failed to copy link');
        }
    };

    const [boardTab, setBoardTab] = useState<'my-boards' | 'shared'>('my-boards');

    // ... (existing code)

    const getFilteredBoards = () => {
        let baseBoards = boards;
        switch (activeTab) {
            case 'recent': baseBoards = boards.filter(b => !b.isDeleted); break;
            case 'favorites': baseBoards = boards.filter(b => !b.isDeleted && b.isFavorite); break;
            case 'trash': baseBoards = boards.filter(b => b.isDeleted); break;
            case 'dashboard':
            default: baseBoards = boards.filter(b => !b.isDeleted);
        }

        // Apply Tab Filter (My Boards vs Shared) - Only for dashboard
        if (activeTab === 'dashboard') {
            if (boardTab === 'my-boards') {
                baseBoards = baseBoards.filter(b => b.user_id === userProfile?.id || !b.user_id);
            } else {
                baseBoards = baseBoards.filter(b => b.user_id && b.user_id !== userProfile?.id);
            }
        }


        // Filter by active workspace (if not default/null)
        if (activeWorkspaceId) {
            const activeWs = workspaces.find(w => w.id === activeWorkspaceId);
            if (activeWs && !activeWs.isDefault) {
                baseBoards = baseBoards.filter(b => activeWs.boardIds.includes(b.id));
            }
        }

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            baseBoards = baseBoards.filter(b =>
                b.title.toLowerCase().includes(q) ||
                (b.description && b.description.toLowerCase().includes(q))
            );
        }

        // Apply Color Filter
        if (filterColor) {
            baseBoards = baseBoards.filter(b => (b.themeColor || 'blue') === filterColor);
        }

        // Apply Sorting
        return [...baseBoards].sort((a, b) => {
            let comparison = 0;
            switch (sortBy) {
                case 'title':
                    comparison = a.title.localeCompare(b.title);
                    break;
                case 'created':
                    comparison = new Date(a.lastEdited || 0).getTime() - new Date(b.lastEdited || 0).getTime();
                    break;
                case 'itemCount':
                    comparison = (a.itemCount ?? a.notes?.length ?? 0) - (b.itemCount ?? b.notes?.length ?? 0);
                    break;
                case 'lastEdited':
                default:
                    comparison = new Date(a.lastEdited).getTime() - new Date(b.lastEdited).getTime();
            }
            return sortOrder === 'asc' ? comparison : -comparison;
        });
    };

    const filteredBoards = getFilteredBoards();
    // Count stats for tabs
    const myBoardsCount = boards.filter(b => !b.isDeleted && (b.user_id === userProfile?.id || !b.user_id)).length;
    const sharedBoardsCount = boards.filter(b => !b.isDeleted && (b.user_id && b.user_id !== userProfile?.id)).length;

    const isTrash = activeTab === 'trash';
    const isSettings = activeTab === 'settings';
    const isSmartView = activeTab === 'smartview';
    const isPromptLibrary = activeTab === 'prompts';

    return (
        <div className="flex h-full w-full bg-[#0a0a0a] text-slate-900 dark:text-slate-100 font-sans overflow-hidden transition-colors duration-300">

            {/* Sidebar */}
            <Sidebar
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                isCollapsed={isCollapsed}
                setIsCollapsed={setIsCollapsed}
                onCreateBoard={onCreateBoard}
                userProfile={userProfile}
                currentTheme={currentTheme}
                onThemeChange={onThemeChange}
                onLogout={onLogout}
                isProfileMenuOpen={isProfileMenuOpen}
                setIsProfileMenuOpen={setIsProfileMenuOpen}
                showThemeOptions={showThemeOptions}
                setShowThemeOptions={setShowThemeOptions}
                profileMenuRef={profileMenuRef}
                workspaces={workspaces}
                activeWorkspaceId={activeWorkspaceId}
                onSelectWorkspace={onSelectWorkspace}
                onCreateWorkspace={onCreateWorkspace}
                onDeleteWorkspace={onDeleteWorkspace}
                onRenameWorkspace={onRenameWorkspace}
                workspaceColors={workspaceColors}
            />

            {/* Main Content */}
            <main className={`flex-1 w-full relative bg-[#0a0a0a] transition-all duration-500 pb-24 lg:pb-0 ${(isSmartView || isPromptLibrary) ? 'overflow-hidden flex flex-col' : 'overflow-y-auto thin-scrollbar'} ${isCollapsed ? 'lg:pl-[60px]' : 'lg:pl-80'}`}>
                {isSmartView ? (
                    <Suspense fallback={<LoadingFallback />}>
                        <SmartViewPage boards={boards} />
                    </Suspense>
                ) : isPromptLibrary ? (
                    <Suspense fallback={<LoadingFallback />}>
                        <PromptLibraryPage />
                    </Suspense>
                ) : (
                    <div className="p-6 md:p-8 mx-auto w-full">
                        {/* Header & Quick Actions Area */}
                        {!isSettings && activeTab === 'dashboard' && !searchQuery && (
                            <>
                                <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4">
                                    <DashboardHeader
                                        userProfile={userProfile}
                                        boardsCount={myBoardsCount}
                                        sharedCount={sharedBoardsCount}
                                    />
                                    <div className="flex items-center gap-3 mb-1">
                                        <NotificationBell onSelectBoard={onSelectBoard} />
                                        <GlobalSearch onSelectResult={(boardId, noteId) => {
                                            const board = boards.find(b => b.id === boardId);
                                            if (board && noteId) {
                                                const url = board.slug ? `/board/${board.slug}` : `/?board=${board.id}`;
                                                window.history.pushState({}, '', `${url}?noteId=${noteId}`);
                                            }
                                            onSelectBoard(boardId);
                                        }} />
                                    </div>
                                </div>

                                <div className="mb-8">
                                    <QuickActions
                                        onCreateBoard={onCreateBoard}
                                        onImportBoard={_onImportBoard}
                                        onOpenTemplateWizard={() => setIsTemplateWizardOpen(true)}
                                    />
                                </div>

                                <RecentlyViewed
                                    boards={boards.filter(b => !b.isDeleted)}
                                    onSelectBoard={onSelectBoard}
                                />
                            </>
                        )}

                        {/* Title for non-dashboard tabs or search */}
                        {(isSettings || activeTab !== 'dashboard' || searchQuery) && (
                            <div className="flex flex-col md:flex-row justify-between md:items-end mb-10 animate-in slide-in-from-top-4 duration-500 gap-4">
                                <div>
                                    <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-2 text-slate-900 dark:text-white">
                                        {isSettings ? 'Settings' : activeTab === 'favorites' ? 'Favorites' : activeTab === 'trash' ? 'Trash' : greeting}
                                    </h1>
                                    <p className="text-slate-500 dark:text-slate-400 font-medium text-lg">
                                        {activeTab === 'favorites' ? 'Your pinned collection.' : activeTab === 'trash' ? 'Deleted boards.' : activeTab === 'settings' ? 'Manage your account and canvas.' : "Here is what's happening today."}
                                    </p>
                                </div>
                                <div className="self-start md:self-auto flex items-center gap-4">
                                    {!isSettings && <NotificationBell onSelectBoard={onSelectBoard} />}
                                    {!isSettings && <GlobalSearch onSelectResult={(boardId, noteId) => {
                                        const board = boards.find(b => b.id === boardId);
                                        if (board && noteId) {
                                            const url = board.slug ? `/board/${board.slug}` : `/?board=${board.id}`;
                                            window.history.pushState({}, '', `${url}?noteId=${noteId}`);
                                        }
                                        onSelectBoard(boardId);
                                    }} />}
                                </div>
                            </div>
                        )}

                        {isSettings ? (
                            <SettingsPanel
                                userProfile={userProfile}
                                onUpdateProfile={onUpdateProfile}
                                canvasSettings={canvasSettings}
                                onCanvasSettingsChange={onCanvasSettingsChange}
                                currentTheme={currentTheme}
                                onThemeChange={onThemeChange}
                            />
                        ) : (
                            <>
                                <div className="flex items-center justify-between mb-8 mt-12 pt-8 border-t border-slate-100 dark:border-neutral-800">
                                    {/* Left Side: Tabs or Active Title */}
                                    {activeTab === 'dashboard' ? (
                                        <div className="flex flex-col gap-4">
                                            <SectionTitle title="Exploration" subtitle="Browse all your creative spaces" />
                                            <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-[#1a1a1a]/50 rounded-xl w-fit">
                                                <button
                                                    onClick={() => setBoardTab('my-boards')}
                                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${boardTab === 'my-boards'
                                                        ? 'bg-white dark:bg-[#121212] text-slate-900 dark:text-white shadow-sm'
                                                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                                        }`}
                                                >
                                                    <Icons.Grid className="w-4 h-4" />
                                                    My Boards
                                                    <span className="ml-1 opacity-60">{myBoardsCount}</span>
                                                </button>
                                                <button
                                                    onClick={() => setBoardTab('shared')}
                                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${boardTab === 'shared'
                                                        ? 'bg-white dark:bg-[#121212] text-slate-900 dark:text-white shadow-sm'
                                                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                                        }`}
                                                >
                                                    <Icons.Users className="w-4 h-4" />
                                                    Shared
                                                    {sharedBoardsCount > 0 && <span className="ml-1 opacity-60">{sharedBoardsCount}</span>}
                                                </button>
                                            </div>

                                            {/* Color Filter Chips */}
                                            <div className="flex items-center gap-2 mt-2 overflow-x-auto p-2 scrollbar-hide max-w-full">
                                                <button
                                                    onClick={() => setFilterColor(null)}
                                                    className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider transition-all flex-shrink-0 ${filterColor === null ? 'bg-slate-900 text-white dark:bg-white dark:text-black shadow-lg' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                                                >
                                                    All
                                                </button>
                                                {['blue', 'purple', 'red', 'orange', 'yellow', 'green', 'teal', 'dark'].map(color => (
                                                    <button
                                                        key={color}
                                                        onClick={() => setFilterColor(color)}
                                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider transition-all flex-shrink-0 ${filterColor === color ? 'ring-2 ring-offset-2 ring-slate-900 dark:ring-white scale-105' : 'opacity-70 hover:opacity-100 dark:text-slate-400 dark:hover:text-white'}`}
                                                    >
                                                        <div className={`w-2.5 h-2.5 rounded-full shadow-sm ${THEME_STYLES_CONST[color as keyof typeof THEME_STYLES_CONST]?.liquid.bg || 'bg-slate-400'}`} />
                                                        <span>{color}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-3">
                                            <h2 className="text-xl font-bold capitalize dark:text-white">{activeTab}</h2>
                                            <span className="bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] font-bold px-2 py-1 rounded-md">{filteredBoards.length}</span>
                                        </div>
                                    )}

                                    {!isTrash && (
                                        <div className="flex items-center gap-3">
                                            {/* Sorting Controls */}
                                            <div className="flex items-center gap-2 bg-slate-100 dark:bg-[#1a1a1a]/50 p-1 rounded-xl">
                                                <select
                                                    value={sortBy}
                                                    onChange={(e) => setSortBy(e.target.value as 'lastEdited' | 'created' | 'title' | 'itemCount')}
                                                    className="bg-transparent text-[11px] font-bold px-2 py-1 outline-none border-none cursor-pointer text-slate-600 dark:text-slate-400"
                                                >
                                                    <option value="lastEdited">Last Edited</option>
                                                    <option value="created">Created Date</option>
                                                    <option value="title">Alphabetical</option>
                                                    <option value="itemCount">Item Count</option>
                                                </select>
                                                <button
                                                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                                                    className="p-1.5 hover:bg-white dark:hover:bg-[#121212] rounded-lg transition-all text-slate-500 hover:text-slate-900 dark:hover:text-white"
                                                    title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                                                >
                                                    {sortOrder === 'asc' ? <Icons.ChevronUp className="w-3.5 h-3.5" /> : <Icons.ChevronDown className="w-3.5 h-3.5" />}
                                                </button>
                                            </div>

                                            <div className="flex gap-2">
                                                {/* View Toggles */}
                                                <button
                                                    onClick={() => setViewMode('grid')}
                                                    className={`p-2 border rounded-xl transition-all shadow-sm ${viewMode === 'grid' ? 'bg-white dark:bg-[#121212] border-stone-300 dark:border-slate-600 text-slate-900 dark:text-white' : 'bg-transparent border-transparent text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
                                                >
                                                    <Icons.Grid className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => setViewMode('list')}
                                                    className={`p-2 border rounded-xl transition-all shadow-sm ${viewMode === 'list' ? 'bg-white dark:bg-[#121212] border-stone-300 dark:border-slate-600 text-slate-900 dark:text-white' : 'bg-transparent border-transparent text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
                                                >
                                                    <Icons.List className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {filteredBoards.length === 0 && (
                                    <div className="flex flex-col items-center justify-center py-32 animate-in fade-in zoom-in-95 duration-500">
                                        <div className="relative group mb-6">
                                            <div className="absolute inset-0 bg-indigo-500/20 rounded-full blur-2xl group-hover:blur-3xl transition-all duration-500" />
                                            <div className="relative w-24 h-24 bg-gradient-to-br from-slate-100 to-white dark:from-slate-800 dark:to-slate-900 rounded-3xl shadow-xl flex items-center justify-center border border-white/50 dark:border-white/10 rotate-12 group-hover:rotate-6 transition-transform duration-500">
                                                {isTrash ? <Icons.Trash className="w-10 h-10 text-slate-400 dark:text-slate-500" /> : <Icons.LayoutGrid className="w-10 h-10 text-indigo-500/50" />}
                                            </div>
                                            {/* Floating elements */}
                                            {!isTrash && (
                                                <>
                                                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-500 rounded-xl shadow-lg flex items-center justify-center text-white rotate-12 animate-bounce delay-100">
                                                        <Icons.Plus className="w-4 h-4" />
                                                    </div>
                                                    <div className="absolute -bottom-2 -left-2 w-8 h-8 bg-purple-500 rounded-xl shadow-lg flex items-center justify-center text-white -rotate-12 animate-bounce delay-300">
                                                        <Icons.Star className="w-4 h-4" />
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                                            {isTrash ? "Trash is empty" : activeTab === 'favorites' ? "No favorites yet" : searchQuery ? "No matches found" : "Start your journey"}
                                        </h3>
                                        <p className="text-slate-500 dark:text-slate-400 max-w-sm text-center mb-8 text-sm leading-relaxed">
                                            {isTrash
                                                ? "Items you delete will appear here. You can restore them anytime."
                                                : activeTab === 'favorites'
                                                    ? "Pin your most important boards here for quick access."
                                                    : searchQuery
                                                        ? `We couldn't find any boards matching "${searchQuery}".`
                                                        : "Create your first board to start capturing ideas, planning projects, and organizing your thoughts."}
                                        </p>

                                        {!isTrash && !searchQuery && activeTab === 'dashboard' && (
                                            <button
                                                onClick={onCreateBoard}
                                                className="group relative px-6 py-3 bg-slate-900 dark:bg-white text-white dark:text-black rounded-xl font-bold text-sm shadow-xl shadow-indigo-500/10 hover:shadow-indigo-500/20 hover:scale-105 transition-all outline-none focus:ring-4 focus:ring-indigo-500/20"
                                            >
                                                <span className="flex items-center gap-2">
                                                    <Icons.Plus className="w-4 h-4" />
                                                    Create New Board
                                                </span>
                                            </button>
                                        )}

                                        {activeTab === 'favorites' && (
                                            <button
                                                onClick={() => setActiveTab('dashboard')}
                                                className="text-indigo-500 font-bold text-sm hover:underline"
                                            >
                                                Browse all boards
                                            </button>
                                        )}
                                    </div>
                                )}

                                {viewMode === 'grid' ? (
                                    <BoardGrid
                                        boards={filteredBoards}
                                        userProfile={userProfile}
                                        activeTab={activeTab}
                                        isTrash={isTrash}
                                        searchQuery={searchQuery}
                                        boardTab={boardTab}
                                        openMenuId={openMenuId}
                                        setOpenMenuId={setOpenMenuId}
                                        onSelectBoard={onSelectBoard}
                                        onCreateBoard={onCreateBoard}
                                        onCreateBoardFromTemplate={onCreateBoardFromTemplate}
                                        onOpenTemplateWizard={() => setIsTemplateWizardOpen(true)}
                                        onToggleFavorite={onToggleFavorite}
                                        setEditingBoard={setEditingBoard}
                                        setDeleteConfirmId={setDeleteConfirmId}
                                        onRestoreBoard={onRestoreBoard}
                                        onDeleteForever={onDeleteForever}

                                        onDuplicateBoard={onDuplicateBoard || (() => { })}
                                        onShareBoard={handleShareBoard}
                                        showCreateCard={boardTab === 'my-boards' && activeTab === 'dashboard' && !searchQuery}
                                    />
                                ) : (
                                    <BoardList
                                        boards={filteredBoards}
                                        openMenuId={openMenuId}
                                        setOpenMenuId={setOpenMenuId}
                                        onSelectBoard={onSelectBoard}
                                        onToggleFavorite={onToggleFavorite}
                                        setEditingBoard={setEditingBoard}
                                        setDeleteConfirmId={setDeleteConfirmId}
                                        onRestoreBoard={onRestoreBoard}
                                        onDeleteForever={onDeleteForever}
                                        isTrash={isTrash}
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}
            </main>

            {/* Robust Edit Board Modal */}
            {
                editingBoard && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/20 dark:bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-white dark:bg-[#121212] rounded-3xl shadow-2xl p-8 w-full max-w-md relative animate-in zoom-in-95 duration-200 border border-slate-200 dark:border-neutral-800">
                            <button onClick={() => setEditingBoard(null)} className="absolute top-6 right-6 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-neutral-800 transition-colors"><Icons.X className="w-5 h-5 text-slate-400" /></button>
                            <h2 className="text-2xl font-black mb-6 tracking-tight text-slate-900 dark:text-white">Edit Board</h2>
                            <form onSubmit={handleEditSubmit} className="space-y-5">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-neutral-500 mb-2">Board Name</label>
                                    <input
                                        autoFocus
                                        type="text"
                                        value={editingBoard.title}
                                        onChange={(e) => setEditingBoard({ ...editingBoard, title: e.target.value })}
                                        className="w-full bg-white dark:bg-[#1a1a1a] border border-slate-200 dark:border-neutral-800 rounded-xl px-4 py-3 font-bold text-slate-900 dark:text-white focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 transition-all shadow-sm"
                                        placeholder="My Creative Board"
                                    />
                                    {/* Slug Preview */}
                                    <p className="mt-2 text-xs text-slate-400 dark:text-neutral-500 truncate">
                                        <span className="opacity-70">URL:</span>{' '}
                                        <span className="font-mono">/board/{generateSlug(editingBoard.title) || 'untitled'}</span>
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-neutral-500 mb-2">Description</label>
                                    <textarea
                                        rows={3}
                                        value={editingBoard.description || ''}
                                        onChange={(e) => setEditingBoard({ ...editingBoard, description: e.target.value })}
                                        className="w-full bg-white dark:bg-[#1a1a1a] border border-slate-200 dark:border-neutral-800 rounded-xl px-4 py-3 font-medium text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 transition-all resize-none shadow-sm"
                                        placeholder="What's this board about?"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-neutral-500 mb-2">Theme Color</label>
                                    <div className="flex gap-3">
                                        {Object.keys(THEME_STYLES_CONST).map(c => (
                                            <button
                                                key={c}
                                                type="button"
                                                onClick={() => setEditingBoard({ ...editingBoard, themeColor: c })}
                                                className={`w-9 h-9 rounded-full transition-all border-2 ${editingBoard.themeColor === c ? 'border-black dark:border-white scale-110 shadow-md' : 'border-transparent scale-95 opacity-70 hover:opacity-100 hover:scale-100'}`}
                                                style={{ backgroundColor: c === 'white' ? '#f1f5f9' : c }}
                                                title={c}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <div className="pt-4 flex justify-end gap-3">
                                    <button type="button" onClick={() => setEditingBoard(null)} className="px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest text-slate-500 hover:bg-slate-100 dark:hover:bg-neutral-800 transition-all">Cancel</button>
                                    <button type="submit" className="px-6 py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest bg-slate-900 dark:bg-white text-white dark:text-black hover:bg-black dark:hover:bg-slate-200 transition-all shadow-lg active:scale-95">Save Changes</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {
                deleteConfirmId && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/20 dark:bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-white dark:bg-[#121212] rounded-3xl shadow-2xl p-8 w-full max-w-sm relative animate-in zoom-in-95 duration-200 text-center border border-slate-200 dark:border-neutral-800">
                            <div className="w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4 text-red-500 shadow-sm"><Icons.Trash className="w-7 h-7" /></div>
                            <h3 className="text-xl font-black mb-2 text-slate-900 dark:text-white tracking-tight">Move to Trash?</h3>
                            <p className="text-slate-500 dark:text-slate-400 font-medium mb-8 text-sm leading-relaxed px-2">Don't worry, you can restore this board from the Trash tab later if you change your mind.</p>
                            <div className="flex gap-3">
                                <button onClick={() => setDeleteConfirmId(null)} className="flex-1 px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-[#1a1a1a] hover:bg-slate-200 dark:hover:bg-neutral-800 transition-all">Cancel</button>
                                <button onClick={() => { onDeleteBoard(deleteConfirmId); setDeleteConfirmId(null); }} className="flex-1 px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest bg-red-500 text-white hover:bg-red-600 transition-all shadow-lg shadow-red-500/20 active:scale-95">Move to Trash</button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Mobile Bottom Navigation - Only visible on small screens */}
            <nav className="fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-t border-stone-200 dark:border-slate-800 z-50 lg:hidden pb-safe">
                <div className="flex items-center justify-around py-2 px-4">
                    <button
                        onClick={() => setActiveTab('dashboard')}
                        className={`flex flex-col items-center gap-1 py-2 px-4 rounded-xl transition-all ${activeTab === 'dashboard' ? 'text-blue-500' : 'text-slate-400'}`}
                    >
                        <Icons.Grid className="w-6 h-6" />
                        <span className="text-[10px] font-bold">Home</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('favorites')}
                        className={`flex flex-col items-center gap-1 py-2 px-4 rounded-xl transition-all ${activeTab === 'favorites' ? 'text-blue-500' : 'text-slate-400'}`}
                    >
                        <Icons.Star className="w-6 h-6" />
                        <span className="text-[10px] font-bold">Favorites</span>
                    </button>
                    <button
                        onClick={onCreateBoard}
                        className="w-14 h-14 -mt-6 bg-black dark:bg-white text-white dark:text-black rounded-full shadow-xl flex items-center justify-center"
                    >
                        <Icons.Plus className="w-7 h-7" />
                    </button>
                    <button
                        onClick={() => setActiveTab('trash')}
                        className={`flex flex-col items-center gap-1 py-2 px-4 rounded-xl transition-all ${activeTab === 'trash' ? 'text-blue-500' : 'text-slate-400'}`}
                    >
                        <Icons.Trash className="w-6 h-6" />
                        <span className="text-[10px] font-bold">Trash</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('settings')}
                        className={`flex flex-col items-center gap-1 py-2 px-4 rounded-xl transition-all ${activeTab === 'settings' ? 'text-blue-500' : 'text-slate-400'}`}
                    >
                        <Icons.Settings className="w-6 h-6" />
                        <span className="text-[10px] font-bold">Settings</span>
                    </button>
                </div>
            </nav>

            <TemplateWizard
                isOpen={isTemplateWizardOpen}
                onClose={() => setIsTemplateWizardOpen(false)}
                onCreateBoardFromTemplate={(template) => {
                    onCreateBoardFromTemplate?.(template);
                }}
            />
        </div >
    );
}